import React, {
  useEffect,
  useState,
  useRef,
  createRef,
  useCallback,
  useLayoutEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import { ImageSquare } from '@phosphor-icons/react';

import {
  EnrollmentContext,
  ENROLLMENT_COMPLETE_STATUS,
  useConfigurationQuery,
  useEnrollmentContextQuery,
} from '../../../theSource/queries';
import { useEnrollmentStatusQuery } from '../../../enrollment/queries';
import {
  selectActiveHeader,
  selectHeaderItems,
  selectMaxHeaderItem,
} from '../Header/components/EnrollmentNavigation/services';
import {
  ScrollStyled,
  FooterStyled,
  ExpandStyled,
  ComparePlanDetailsMenuStyled,
  CollapseStyled,
  PlanCompareListContainerStyled,
  PlanCompareDetailsStyled,
  PlanCompareCarrierNameStyled,
} from './BottomNavigationStyled';
import {
  selectEnrollmentLatest,
  setEnrollmentLatest,
  setTotalSubNavItems,
  setCurrentSubNavItem,
  setMaxSubNavItem,
  selectProfileSteps,
} from './services';
import {
  DECISION_SUPPORT_PAGES,
  getMaxFooterPage,
  PROFILE_FOOTERS_IN_ORDER,
  isNewStatus,
} from './utils';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { PsButtonTuner } from 'stencilSetup';
import { BenefitPlanDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { benefitCategorySelector } from 'enrollment/services/BenefitCategories/selectors';
import { ROUTES } from 'enrollment/app/routes';

export interface BottomNavProps {
  completePages?: string[];
  onPage?: string;
  futurePages?: string[];
  translatedPage?: string;
  onClickEvent?: () => void;
  children?: JSX.Element;
  comparePlansFooter?: boolean;
  optionalClass?: string;
  comparePlans?: BenefitPlanDto[];
  orgBenefitId?: number;
  disableButton?: boolean;
  buttonType?: 'primary' | 'secondary';
  altContinueBtnText?: string;
  skipStatusUpdater?: boolean;
}

export const BottomNavigation = ({
  completePages,
  onPage,
  futurePages,
  translatedPage,
  onClickEvent,
  children,
  comparePlansFooter,
  optionalClass,
  comparePlans,
  orgBenefitId,
  altContinueBtnText,
  buttonType = 'primary',
  disableButton = false,
  skipStatusUpdater = false,
}: BottomNavProps) => {
  const { t } = useTranslation(translatedPage);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const enrollmentLatest = useSelector(selectEnrollmentLatest);
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const activeHeader = useSelector(selectActiveHeader);
  const maxHeaderItem = useSelector(selectMaxHeaderItem);
  // const coveragesByCategory = useSelector(selectCoveragesByCategory);
  const navHeaderItems = useSelector(selectHeaderItems);
  const profileSteps = useSelector(selectProfileSteps);
  const benefitCategories = useSelector(benefitCategorySelector);
  const theme = useTheme();
  const isScreenSmall = useMediaQuery(theme.breakpoints.down(744));

  const [disableContinueBtn, setDisableContinueBtn] = useState(true);
  const [numCoverages, setNumCoverages] = useState(0);
  const [savedCompletePages, setSavedCompletePages] = useState<
    (string | undefined)[]
  >([]);
  const [savedOnPage, setSavedOnPage] = useState<string | undefined>('');
  const [savedFuturePages, setSavedFuturePages] = useState<
    (string | undefined)[]
  >([]);
  const allPages = [completePages, onPage, futurePages].flat(2);
  const onPageIndex = completePages ? completePages.length + 1 : 2;

  dispatch(setCurrentSubNavItem(onPageIndex));
  dispatch(
    setMaxSubNavItem(savedCompletePages ? savedCompletePages.length + 1 : 2)
  );
  dispatch(setTotalSubNavItems(allPages));

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const { enrollmentContext: enrollmentContextResponse } =
    useEnrollmentContextQuery({
      enabled: true,
      apiHost: String(lumosApiHost),
    });

  const { EnrollmentStatusRequest } = useEnrollmentStatusQuery({
    lumosApiHost: String(lumosApiHost),
  });

  const navTopath = (route: string) => {
    if (route === profileSteps.decisionSupport) {
      navigate(`/enrollment/${ROUTES.enrollment.decisionSupportRouter.path}`);
    } else {
      navigate(`/enrollment/${route}`);
    }
  };

  const handleOnClick = () => {
    // the Last accessed page is the most recently seen page by the user so if they click lets continue on the profile page
    // we would expect the last accessed page to be 'My family' since that is the page they are loading into
    let setStatus: string | undefined = '';
    if (
      !skipStatusUpdater &&
      (onPage !== 'profile' || enrollmentLatest === 'profile')
    ) {
      setStatus = allPages && allPages[onPageIndex];
      if (
        (setStatus && DECISION_SUPPORT_PAGES.includes(setStatus)) ||
        (!setStatus &&
          activeHeader === 0 &&
          PROFILE_FOOTERS_IN_ORDER.includes(enrollmentLatest))
      ) {
        setStatus = 'decision_support';
      } else if (setStatus === undefined) {
        const stepperNum = activeHeader + 1;
        if (stepperNum === 1) {
          setStatus = benefitCategories[0].lookup_code;
        } else {
          setStatus = navHeaderItems[stepperNum];
        }
      }
      if (isNewStatus(setStatus, enrollmentLatest)) {
        EnrollmentStatusRequest({
          enrollment_type: enrollmentContext,
          status: String(setStatus),
        });
        dispatch(setEnrollmentLatest(String(setStatus)));
      }
    }
    if (onClickEvent) {
      onClickEvent();
    }
  };

  const displayCheckIcon = (name: string, btnRef: string) => {
    // TODO_GL: This is placeholder code for when other enrollment flows are implemented
    // standard flow - add the standard flow toggle to this when added
    switch (activeHeader) {
      case 2:
      case 3:
        return !disableButton ? 'check' : '';
      default:
        return btnRef === 'onPage' ? '' : 'check';
    }
  };

  const [openPlanCompareList, setOpenPlanCompareList] = useState(false);
  const togglePlanCompareList = () => {
    setOpenPlanCompareList(!openPlanCompareList);
  };
  const removePlanFromMobileCompare = (
    planId: number,
    orgBenefitId?: number
  ) => {
    if (planId) {
      // Add a check to ensure that the index is not undefined
      document.getElementById(`checkbox-${planId}-${orgBenefitId}`)?.click();
    }
  };

  let requiredTasks: string[] = [];
  if (enrollmentContextResponse) {
    const contexts = enrollmentContextResponse?.data as EnrollmentContext[];
    const activeContextIndex = Math.max(
      contexts.findIndex((context) => {
        return !ENROLLMENT_COMPLETE_STATUS.includes(context.status);
      }),
      0
    );
    requiredTasks = contexts[activeContextIndex].required_tasks;
  }

  useEffect(() => {
    const maxItem = getMaxFooterPage(
      enrollmentLatest,
      requiredTasks[0],
      activeHeader,
      maxHeaderItem,
      allPages
    );
    if (maxItem === allPages.length - 1) {
      setSavedCompletePages(allPages);
      setSavedOnPage(undefined);
      setSavedFuturePages([undefined]);
    } else if (maxItem !== undefined) {
      setSavedCompletePages(allPages.slice(0, maxItem));
      setSavedOnPage(allPages.slice(maxItem, maxItem + 1)[0]);
      setSavedFuturePages(allPages.slice(maxItem + 1));
    } else {
      setSavedCompletePages([completePages].flat(2));
      setSavedOnPage(onPage);
      setSavedFuturePages([futurePages].flat(2));
    }
    // Exhaustive deps will force a re-render infinitely, so I'm making this exception
    // for the intended functionality
    // eslint-disable-next-line
  }, [completePages, onPage, futurePages, enrollmentLatest]);

  const buttonRefs = useRef<
    Map<string, React.RefObject<HTMLPsButtonTunerElement>>
  >(new Map());

  const getButtonRef = useCallback((name: string) => {
    if (!buttonRefs.current.has(name)) {
      buttonRefs.current.set(name, createRef<HTMLPsButtonTunerElement>());
    }
    return buttonRefs.current.get(name);
  }, []);

  const bottomNavStatus = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    setTimeout(() => {
      for (const activeButtonRef of buttonRefs.current.values()) {
        const activeButton = activeButtonRef.current;
        const extraClassAttr =
          activeButton?.attributes.getNamedItem('extra-class');
        if (extraClassAttr && extraClassAttr.value.includes('active')) {
          const offset = activeButton?.getBoundingClientRect();
          if (offset && bottomNavStatus.current) {
            bottomNavStatus.current.scrollTo(
              offset.left + bottomNavStatus.current.scrollLeft - 60,
              0
            );
          }
        }
      }
    }, 25);
  }, [getButtonRef, savedOnPage]);

  useEffect(() => {
    setDisableContinueBtn(disableButton);
  }, [allPages, savedFuturePages, disableButton]);

  return (
    <FooterStyled
      className={`fixed-bottom ${optionalClass}`}
      data-testid="enrollment-bottom-content"
      id="enrollment-bottom-content"
    >
      {!(isScreenSmall && comparePlansFooter) && (
        <>
          {children}
          <ScrollStyled
            id="bottom-nav-status"
            key={numCoverages}
            ref={bottomNavStatus}
          >
            {savedCompletePages.map(
              (name) =>
                name && (
                  <PsButtonTuner
                    ref={getButtonRef(name)}
                    key={name}
                    buttonId={`benefits-continue-button-${name}`}
                    data-testid={`benefits-continue-button-complete-${name}`}
                    label={t(name)}
                    level="secondary"
                    otherData={[
                      {
                        attribute: 'aria-label',
                        value: `${t('common:navigateTo')} ${name} ${t('common:section')} ${
                          displayCheckIcon(name, 'complete') === 'check'
                            ? t('common:completed')
                            : t('common:incomplete')
                        }`,
                      },
                    ]}
                    extraClass={`btn-pill mx-1 overline1 ${
                      onPage === name && activeHeader !== 1 ? 'active' : ''
                    }`}
                    icon={displayCheckIcon(name, 'complete')}
                    onClick={() => navTopath(name)}
                  />
                )
            )}
            {savedOnPage && (
              <PsButtonTuner
                ref={getButtonRef(savedOnPage)}
                key={savedOnPage}
                buttonId={`benefits-continue-button-${savedOnPage}`}
                data-testid={`benefits-continue-button-on-${savedOnPage}`}
                label={t(savedOnPage)}
                level="secondary"
                extraClass="btn-pill mx-1 overline1"
                icon={displayCheckIcon(savedOnPage, 'onPage')}
                onClick={() => navTopath(savedOnPage)}
              />
            )}
            {savedFuturePages.map(
              (name) =>
                name && (
                  <PsButtonTuner
                    ref={getButtonRef(name)}
                    key={name}
                    buttonId={`benefits-continue-button-${name}`}
                    data-testid={`benefits-continue-button-future-${name}`}
                    label={t(name)}
                    level="secondary"
                    extraClass="btn-pill mx-1 overline1 disabled"
                    disabled={true}
                    onClick={() => navTopath(name)}
                  />
                )
            )}
          </ScrollStyled>
        </>
      )}
      {comparePlansFooter ? (
        <>
          {openPlanCompareList && (
            <PlanCompareListContainerStyled
              id={`mobile-plan-compare-${orgBenefitId}`}
            >
              {comparePlans?.map((plan: BenefitPlanDto, index: number) => (
                <PlanCompareDetailsStyled key={index}>
                  {plan?.carrier_logo_url && plan?.carrier_logo_url !== null ? (
                    <span>
                      <img
                        alt="Carrier Logo"
                        src={plan.carrier_logo_url}
                        width="60"
                      ></img>
                    </span>
                  ) : (
                    <span>
                      <ImageSquare size={24} weight="duotone" color="#56638a" />
                      <PlanCompareCarrierNameStyled>
                        {plan.carrier}
                      </PlanCompareCarrierNameStyled>
                    </span>
                  )}
                  <span role="alert">{plan.plan_name}</span>
                  <button
                    className="drawer-close bg-transparent"
                    onClick={() =>
                      removePlanFromMobileCompare(plan.plan_id, orgBenefitId)
                    }
                  >
                    <i
                      className="ph ph-x"
                      aria-hidden="true"
                      role="presentation"
                    />
                    <span className="sr-only">
                      {t('pages.enrollmentComparePlans:remove')}
                    </span>
                  </button>
                </PlanCompareDetailsStyled>
              ))}
            </PlanCompareListContainerStyled>
          )}
          {isScreenSmall && (
            <ComparePlanDetailsMenuStyled
              onClick={togglePlanCompareList}
              aria-label={t(
                'pages.enrollmentComparePlans:mobileComparePlansButtonScreenReader'
              )}
              aria-controls={`mobile-plan-compare-${orgBenefitId}`}
            >
              <span role="alert">
                {comparePlans?.length}{' '}
                {t('pages.enrollmentComparePlans:mobileComparePlansButton')}{' '}
              </span>
              {openPlanCompareList ? (
                <ExpandStyled></ExpandStyled>
              ) : (
                <CollapseStyled></CollapseStyled>
              )}
            </ComparePlanDetailsMenuStyled>
          )}
          <div>
            <PsButtonTuner
              buttonId="compare-plans-button"
              data-testid="compare-plans-button"
              label={t('compareBtnLabel')}
              level="primary"
              iconRight="arrow-right"
              onClick={handleOnClick}
              disabled={true}
              extraClass="text-nowrap"
            />
          </div>
        </>
      ) : (
        <nav>
          <PsButtonTuner
            buttonId="enrollment-continue-button"
            data-testid="enrollment-continue-button"
            label={altContinueBtnText || t('common:letsContinue')}
            level={buttonType}
            iconRight="arrow-right"
            onClick={handleOnClick}
            disabled={disableContinueBtn}
            extraClass="text-nowrap"
          />
        </nav>
      )}
    </FooterStyled>
  );
};
